import cuid from 'cuid'
import { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Shortlist, ShortlistUploadCompletionTask } from 'types/shortlist'
import type { Track } from 'types/track'
import { uploadProviderFiles } from '@disco/state-management/actions/shortlist-upload-actions'
import { createNewShortlist } from '@disco/state-management/actions/ShortlistActions'
import type { UploadProvider } from 'types/upload'
import {
  getShortlistById,
  getShortlistUploadProgress,
  shortlistHasActiveUploads,
} from '@disco/state-management/selectors/shortlists'
import { omitHiddenSystemFiles } from '@disco/utils/file-utils'
import { pushUINotification } from '@disco/utils/notifications'

type UploadOptions = {
  // The shortlist title
  title?: string
  // What to run after the upload is complete
  uploadCompletionTask?: ShortlistUploadCompletionTask
  // Whether the shortlist is temporary and should be deleted after upload
  temporary?: boolean
  // The context of the upload, e.g. nesting
  context?: string
}

export const useShortlistUpload = (
  onUploaded?: (tracks: Track[]) => void,
): {
  uploadFiles: (
    files: any[],
    provider: UploadProvider,
    options?: UploadOptions,
  ) => Promise<void>
  uploading: boolean
  progress?: number
} => {
  const dispatch = useDispatch<any>()
  // Create a new unique id for each upload session
  const id = useRef(cuid())
  const shortlist: Shortlist | null | undefined = useSelector(state =>
    getShortlistById(state, {
      id: id.current,
    }),
  )
  const uploading: boolean = useSelector(state =>
    shortlistHasActiveUploads(state, {
      id: id.current,
    }),
  )
  const progress = useSelector(state =>
    getShortlistUploadProgress(state, {
      id: id.current,
    }),
  )
  const uploadFiles = useCallback(
    async (files: any[], provider: UploadProvider, options?: UploadOptions) => {
      // Create a new shortlist with the unique id
      // Only create a new shortlist if one doesn't already exist
      if (!shortlist) {
        await dispatch(
          createNewShortlist(undefined, id.current, {
            defaultFields: {
              id: id.current,
              title: options?.title,
              uploadCompletionTask: options?.uploadCompletionTask,
              hidden: options?.temporary,
            },
          }),
        )
      }

      // Upload the files to the provider
      const uploadedShortlist = (await dispatch(
        uploadProviderFiles(
          provider,
          id.current,
          omitHiddenSystemFiles(files),
          pushUINotification,
          {
            context: options?.context,
          },
        ),
      )) as Shortlist | void

      if (uploadedShortlist && onUploaded) {
        const tracks =
          uploadedShortlist.items
            ?.map(item => item.track)
            .filter(track => track.id) || []
        onUploaded(tracks)
      }
    },
    [dispatch, onUploaded, shortlist],
  )
  return {
    uploadFiles,
    uploading,
    progress: shortlist ? progress : undefined,
  }
}
